/**
 * Canonical element-type identifiers used in DocumentElement.typeId.
 * Renderers in each UI package look up implementations from a registry
 * keyed by these strings.
 *
 * Mirrors the 54 file names under
 * apps/web/src/components/documentElements/. Editor-only types
 * (config-* / *-editor) are not listed here — they never appear at
 * fill time.
 */

export const ELEMENT_TYPE = {
  // text inputs
  shortTextInput: "short-text-input",
  longTextInput: "long-text-input",
  nameInput: "name-input",
  numberInput: "number-input",
  emailInput: "email-input",
  phoneInput: "phone-input",
  urlInput: "url-input",
  addressInput: "address-input",
  currency: "currency",
  slider: "slider",
  geoTag: "geo-tag",

  // selection
  dropdownSelect: "dropdown-select",
  checkboxInput: "checkbox-input",
  toggleSwitch: "toggle-switch",
  multiChoice: "multi-choice",
  radioCards: "radio-cards",
  rating: "rating",
  country: "country",
  documentResponseSetPicker: "document-response-set-picker",

  // entity pickers
  managedProperty: "managed-property",
  contact: "contact",
  job: "job",
  asset: "asset",
  user: "user",
  project: "project",
  customer: "customer",
  /** Only inside the Asset element's settings ("Filter by asset class"); in no palette. */
  assetClass: "asset-class",

  // date + time
  datePicker: "date-picker",
  dateRange: "date-range",
  timePicker: "time-picker",
  dateTimePicker: "datetime-picker",

  // media + files
  fileUpload: "file-upload",
  imageUpload: "image-upload",
  signature: "signature",
  fileDownload: "file-download",
  displayImage: "image",

  // layout containers
  pages: "pages",
  page: "page",
  tabs: "tabs",
  tab: "tab",
  section: "section",
  repeatableSection: "repeatable-section",
  lineItems: "line-items",
  columns: "columns",

  // presentation
  heading: "heading",
  paragraph: "paragraph",
  divider: "divider",
  spacer: "spacer",
  logo: "logo",
  // Output documents: starts a new sheet in the PDF.
  pageBreak: "page-break",

  // output-only — appear in form_output_document palettes, not in
  // form_document. Renderers read element.data.bound* payloads that
  // the server bakes in via the renderedOutput resolver.
  formResults: "form-results",
  score: "score",
  eventTimeline: "event-timeline",
  formField: "form-field",
  formStatus: "form-status",
  // The job or managed property the result was filled against, and the
  // stored per-response score counts (see renderOutput.ts).
  detailsTable: "details-table",
  scoreBreakdown: "score-breakdown",
  // The "Linked Entity Data" family: one field of an entity the result
  // was filled against, when there is one. Same shape as form-field, but
  // the value comes from the output context rather than the result.
  // `job-field` is the job report's own element and is NOT one of these;
  // it stays bound to job_report_document only.
  linkedJobField: "linked-job-field",
  linkedPropertyField: "linked-property-field",
  linkedEventField: "linked-event-field",
} as const;

/**
 * Catalogue of form-result statuses surfaced by the `form-status`
 * output element. `signed-off` is a forward-looking state that the
 * sign-off feature will populate; until then it never appears on a
 * real result, but designers still see it as an option.
 */
export const FORM_STATUSES = [
  {
    id: "in-progress",
    label: "In Progress",
  },
  {
    id: "submitted",
    label: "Submitted",
  },
  {
    id: "signed-off",
    label: "Signed Off",
  },
] as const;

export type FormStatusId = (typeof FORM_STATUSES)[number]["id"];

/**
 * Catalogue of form-result metadata fields available to the
 * `form-field` output element. Each entry is its own picker option:
 * the renderer reads `element.data.field` and looks up the matching
 * id below. Server and client both consume this list so the picker,
 * the bound payload, and the mustache placeholders all stay in sync.
 *
 * `placeholderKey` is the mustache key recognised inside
 * heading/paragraph text — e.g. `{{form.startedAt}}` resolves to the
 * same value the form-field element renders.
 */
export const FORM_FIELDS = [
  {
    id: "name",
    label: "Form name",
    placeholderKey: "{{form.name}}",
    type: "text",
  },
  {
    id: "version",
    label: "Form version",
    placeholderKey: "{{form.version}}",
    type: "text",
  },
  {
    id: "startedAt",
    label: "Started",
    placeholderKey: "{{form.startedAt}}",
    type: "date",
  },
  {
    id: "submittedAt",
    label: "Submitted",
    placeholderKey: "{{form.submittedAt}}",
    type: "date",
  },
  {
    id: "duration",
    label: "Duration",
    placeholderKey: "{{form.duration}}",
    type: "text",
  },
  {
    id: "completedBy",
    label: "Completed by",
    placeholderKey: "{{form.completedBy}}",
    type: "text",
  },
  {
    id: "flaggedAnswerCount",
    label: "Flagged answers",
    placeholderKey: "{{form.flaggedAnswerCount}}",
    type: "text",
  },
] as const;

export type FormFieldId = (typeof FORM_FIELDS)[number]["id"];

/**
 * Form fields carrying a date value, so they drive the element's date
 * format picker the way the job and calendar fields do. The placeholder
 * forms (`{{form.startedAt}}`) have no element to read a format off and
 * keep the default rendering.
 */
export const FORM_DATE_FIELD_IDS: readonly string[] = FORM_FIELDS.filter(
  (f) => f.type === "date",
).map((f) => f.id);

/**
 * The canonical job-field vocabulary for `job-field` report elements
 * (job_report_document) — the job-report analog of FORM_FIELDS. The
 * ISM configForm seed's field options, the binder, and the renderers
 * all key off these ids; `type: "date"` fields get the date-format
 * picker in the element's settings.
 */
export const JOB_FIELDS = [
  { id: "numberAndName", label: "Job number and name", type: "text" },
  { id: "name", label: "Job name", type: "text" },
  { id: "jobNumber", label: "Job number", type: "text" },
  { id: "refId", label: "Reference", type: "text" },
  { id: "status", label: "Status", type: "text" },
  { id: "description", label: "Description", type: "richText" },
  {
    id: "leadText",
    label: "Opportunity description for suppliers",
    type: "richText",
  },
  {
    id: "workOrderText",
    label: "Scope of works for suppliers",
    type: "richText",
  },
  {
    id: "descriptionForCustomer",
    label: "Description visible to customer",
    type: "richText",
  },
  { id: "address", label: "Address", type: "text" },
  { id: "customer", label: "Customer", type: "text" },
  { id: "managedProperty", label: "Managed property", type: "text" },
  { id: "siteContact", label: "Site contact", type: "text" },
  { id: "createdDate", label: "Created", type: "date" },
  { id: "dueDate", label: "Due", type: "date" },
  { id: "completedDate", label: "Completed", type: "date" },
] as const;

export type JobFieldId = (typeof JOB_FIELDS)[number]["id"];

/** Job fields that carry a date value (drive the format picker). */
export const JOB_DATE_FIELD_IDS: readonly string[] = JOB_FIELDS.filter(
  (f) => f.type === "date",
).map((f) => f.id);

/**
 * Job fields whose value is formatted text (the four job descriptions):
 * the binder carries their sanitised HTML beside the plain reading so a
 * renderer can keep their lists and line breaks.
 */
export const JOB_RICH_TEXT_FIELD_IDS: readonly string[] = JOB_FIELDS.filter(
  (f) => f.type === "richText",
).map((f) => f.id);

/**
 * Managed-property fields a `details-table` can show: the property the
 * result was filled against, directly or through its job.
 */
export const PROPERTY_FIELDS = [
  { id: "nameWithId", label: "Property name and ID" },
  { id: "name", label: "Property name" },
  { id: "fullId", label: "Property ID" },
  { id: "address", label: "Property address" },
  { id: "customer", label: "Property customer" },
  { id: "contactName", label: "Property contact" },
  { id: "contactPhone", label: "Property contact phone" },
  { id: "contactEmail", label: "Property contact email" },
  { id: "description", label: "Property description" },
] as const;

export type PropertyFieldId = (typeof PROPERTY_FIELDS)[number]["id"];

/**
 * Calendar-event fields a `linked-event-field` can show: the event the
 * result was filled against. Columns of em_calendar_event.
 *
 * Deliberately absent, because the model has no such column: an all-day
 * flag, a location (the location IS the address), a description (that is
 * the notes) and any status beyond finalised. Attendees come from
 * em_calendar_event_entity, not the deprecated single-assignee user_id.
 */
export const CALENDAR_EVENT_FIELDS = [
  { id: "name", label: "Event name", type: "text" },
  { id: "type", label: "Event type", type: "text" },
  { id: "startAt", label: "Event start", type: "date" },
  { id: "endAt", label: "Event end", type: "date" },
  { id: "duration", label: "Event duration", type: "text" },
  { id: "address", label: "Event address", type: "text" },
  { id: "assignedTo", label: "Event assigned to", type: "text" },
  { id: "notes", label: "Event notes", type: "text" },
  { id: "siteVisitRequired", label: "Site visit required", type: "text" },
  { id: "finalised", label: "Event finalised", type: "text" },
] as const;

export type CalendarEventFieldId = (typeof CALENDAR_EVENT_FIELDS)[number]["id"];

/** Event fields that carry a date value (drive the format picker). */
export const CALENDAR_EVENT_DATE_FIELD_IDS: readonly string[] =
  CALENDAR_EVENT_FIELDS.filter((f) => f.type === "date").map((f) => f.id);

/** Where a `details-table` row reads its value from. */
export type DetailsFieldSource = "form" | "job" | "property";

export interface DetailsFieldOption {
  /** The stored value: `<source>.<fieldId>`, e.g. `job.numberAndName`. */
  id: string;
  source: DetailsFieldSource;
  fieldId: string;
  /** Picker label, prefixed with its source so same-named fields differ. */
  label: string;
  /** Default row label printed in the table. */
  rowLabel: string;
}

const FORM_ROW_LABELS: Record<string, string> = {
  name: "Form",
  version: "Version",
  startedAt: "Started",
  submittedAt: "Submitted",
  duration: "Duration",
  completedBy: "Completed by",
  flaggedAnswerCount: "Flagged answers",
};

const JOB_ROW_LABELS: Record<string, string> = {
  numberAndName: "Job",
  name: "Job name",
  jobNumber: "Job number",
  refId: "Reference",
  status: "Job status",
  description: "Description",
  leadText: "Opportunity description",
  workOrderText: "Scope of works",
  descriptionForCustomer: "Customer description",
  address: "Address",
  customer: "Customer",
  managedProperty: "Property",
  siteContact: "Site contact",
  createdDate: "Created",
  dueDate: "Due",
  completedDate: "Completed",
};

const PROPERTY_ROW_LABELS: Record<string, string> = {
  nameWithId: "Property",
  name: "Property",
  fullId: "Property ID",
  address: "Property address",
  customer: "Customer",
  contactName: "Contact",
  contactPhone: "Contact phone",
  contactEmail: "Contact email",
  description: "Property description",
};

/** "Job: Number and name": the source, then the field without its source word. */
function pickerLabel(source: string, label: string): string {
  const bare = label.replace(new RegExp(`^${source} `), "");
  return `${source}: ${bare.charAt(0).toUpperCase()}${bare.slice(1)}`;
}

/**
 * Every field a `details-table` offers, Job first, then Property, then
 * Form. The ISM configForm seed's options mirror this list.
 */
export const DETAILS_FIELDS: readonly DetailsFieldOption[] = [
  ...JOB_FIELDS.map((f) => ({
    id: `job.${f.id}`,
    source: "job" as const,
    fieldId: f.id,
    label: pickerLabel("Job", f.label),
    rowLabel: JOB_ROW_LABELS[f.id] ?? f.label,
  })),
  ...PROPERTY_FIELDS.map((f) => ({
    id: `property.${f.id}`,
    source: "property" as const,
    fieldId: f.id,
    label: pickerLabel("Property", f.label),
    rowLabel: PROPERTY_ROW_LABELS[f.id] ?? f.label,
  })),
  ...FORM_FIELDS.map((f) => ({
    id: `form.${f.id}`,
    source: "form" as const,
    fieldId: f.id,
    label: pickerLabel("Form", f.label),
    rowLabel: FORM_ROW_LABELS[f.id] ?? f.label,
  })),
];

/** How a `score-breakdown` row prints its count. */
export const SCORE_BREAKDOWN_DISPLAYS = [
  { id: "countOfTotal", label: "Count of total (33 / 35)" },
  { id: "count", label: "Count (33)" },
  { id: "percent", label: "Percent (94%)" },
] as const;

export type ScoreBreakdownDisplay =
  (typeof SCORE_BREAKDOWN_DISPLAYS)[number]["id"];

export type ElementTypeId = (typeof ELEMENT_TYPE)[keyof typeof ELEMENT_TYPE];

/** Layout/structural containers — walkers recurse through `elements` */
export const CONTAINER_TYPE_IDS: ReadonlySet<string> = new Set<string>([
  ELEMENT_TYPE.pages,
  ELEMENT_TYPE.page,
  ELEMENT_TYPE.tabs,
  ELEMENT_TYPE.tab,
  ELEMENT_TYPE.section,
  ELEMENT_TYPE.repeatableSection,
  ELEMENT_TYPE.lineItems,
  ELEMENT_TYPE.columns,
]);

/**
 * Elements that capture a filler answer — i.e. those that can meaningfully
 * be "required". Excludes layout containers, presentation elements
 * (heading/paragraph/divider/spacer/logo), the download link + display
 * image, and the output-only elements. Used by the form-level
 * `requiredOverride` so it only forces genuine inputs, never display nodes.
 */
export const ANSWERABLE_TYPE_IDS: ReadonlySet<string> = new Set<string>([
  ELEMENT_TYPE.shortTextInput,
  ELEMENT_TYPE.longTextInput,
  ELEMENT_TYPE.nameInput,
  ELEMENT_TYPE.numberInput,
  ELEMENT_TYPE.emailInput,
  ELEMENT_TYPE.phoneInput,
  ELEMENT_TYPE.urlInput,
  ELEMENT_TYPE.addressInput,
  ELEMENT_TYPE.currency,
  ELEMENT_TYPE.slider,
  ELEMENT_TYPE.geoTag,
  ELEMENT_TYPE.dropdownSelect,
  ELEMENT_TYPE.checkboxInput,
  ELEMENT_TYPE.toggleSwitch,
  ELEMENT_TYPE.multiChoice,
  ELEMENT_TYPE.radioCards,
  ELEMENT_TYPE.rating,
  ELEMENT_TYPE.country,
  ELEMENT_TYPE.documentResponseSetPicker,
  ELEMENT_TYPE.managedProperty,
  ELEMENT_TYPE.contact,
  ELEMENT_TYPE.job,
  ELEMENT_TYPE.asset,
  ELEMENT_TYPE.user,
  ELEMENT_TYPE.project,
  ELEMENT_TYPE.customer,
  ELEMENT_TYPE.datePicker,
  ELEMENT_TYPE.dateRange,
  ELEMENT_TYPE.timePicker,
  ELEMENT_TYPE.dateTimePicker,
  ELEMENT_TYPE.fileUpload,
  ELEMENT_TYPE.imageUpload,
  ELEMENT_TYPE.signature,
]);

/** Sentinel values used inside answer payloads. */
export const ANSWER_SENTINEL = {
  /** Multi-choice "Other" entry — captures a free-text response */
  other: "__OTHER__",
  /** Multi-choice "None of the above" — clears all other picks */
  none: "__NONE__",
} as const;
